import * as React from "react";
import { ChevronDown } from "lucide-react";
import { Namespace, useTypedT } from "@racoon/i18n";
import { cn, Hyperlink } from "@racoon/ui";

type Props = {
  routeId: bigint;
  /** "Рейс N", numbered over the shift's trips; absent, it is left out. */
  tripNumber?: number;
  expanded: boolean;
  onToggle: () => void;
  /** Opens the route card from the "M…" id; without it the id is plain text. */
  onOpenRoute?: () => void;
  /** Next to the id: the route's status. */
  status: React.ReactNode;
  /** The header's centre: the trip's counts. */
  summary?: React.ReactNode;
  /** The header's right side (the menu); a click there does not fold the trip. */
  actions?: React.ReactNode;
  children: React.ReactNode;
};

/** One trip of a route-sheet cascade: its header, then a body that folds away in 200ms. */
export const TripCascadeRow = ({
  routeId,
  tripNumber,
  expanded,
  onToggle,
  onOpenRoute,
  status,
  summary,
  actions,
  children,
}: Props) => {
  const t = useTypedT(Namespace.Routes);
  return (
    <div className="overflow-hidden rounded-[8px] border border-ds-border-primary">
      {/* Figma "Cascade title": trip + status left, counts centred in the space left, menu
          right. Only the counts give way (wrapping) when the map leaves the row narrow. */}
      <div className="flex items-center gap-4 bg-ds-surface-secondary p-2">
        {/* The id is a link of its own, so it sits beside the fold toggle, not inside it. */}
        <div className="flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-bold uppercase leading-4">
          <button
            type="button"
            className="flex items-center gap-2 text-left"
            onClick={onToggle}
            aria-expanded={expanded}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-[4px] hover:bg-ds-surface-tertiary">
              <ChevronDown
                className={cn(
                  "h-5 w-5 text-ds-icon-default transition-transform",
                  expanded ? "rotate-0" : "-rotate-90",
                )}
              />
            </span>
            {tripNumber !== undefined && (
              <span className="text-ds-text-body">
                {t("trip")} {tripNumber}
              </span>
            )}
          </button>
          {onOpenRoute ? (
            <Hyperlink onOpen={onOpenRoute} className="text-ds-text-secondary">
              M{String(routeId)}
            </Hyperlink>
          ) : (
            <span className="text-ds-text-secondary">M{String(routeId)}</span>
          )}
          <div className="ml-2 flex items-center gap-4">{status}</div>
        </div>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-x-8 gap-y-1 whitespace-nowrap px-2 text-xs font-bold uppercase leading-4 text-ds-text-secondary">
          {summary}
        </div>
        <div className="flex shrink-0 items-center gap-4">{actions}</div>
      </div>
      <div
        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
        aria-hidden={!expanded}
      >
        {/* Folded, the body is 0px tall but a border would still double the card's bottom edge. */}
        <div
          className={cn("min-h-0 overflow-hidden", expanded && "border-t border-ds-border-primary")}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
